import React, { useState } from 'react';
import { X, Download, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactFormModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', restaurantName: '',
    smsOptIn: false, emailOptIn: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isValid = form.name.trim() && form.email.trim() && form.phone.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contacts`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        restaurantName: form.restaurantName.trim(),
        smsOptIn: form.smsOptIn,
        emailOptIn: form.emailOptIn,
      });
      onSuccess(form);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="contact-modal-overlay">
      <div className="pizza-card p-6 w-full max-w-md relative" data-testid="contact-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#2A2A2A]/40 hover:text-[#E23D28]" data-testid="contact-modal-close">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center mx-auto mb-3 shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]">
            <Download className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
            Download Your Report
          </h2>
          <p className="text-sm text-[#2A2A2A]/60 mt-1">
            Enter your info and we'll generate a branded PDF you can share with partners, investors, or your management team.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Full Name <span className="text-[#E23D28]">*</span>
            </label>
            <input type="text" required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Mike Romano"
              className="pizza-input w-full text-sm" data-testid="contact-name-input" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Email <span className="text-[#E23D28]">*</span>
            </label>
            <input type="email" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="mike@mypizzashop.com"
              className="pizza-input w-full text-sm" data-testid="contact-email-input" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Phone <span className="text-[#E23D28]">*</span>
            </label>
            <input type="tel" required value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="(555) 123-4567"
              className="pizza-input w-full text-sm" data-testid="contact-phone-input" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Restaurant Name
            </label>
            <input type="text" value={form.restaurantName}
              onChange={e => setForm(f => ({ ...f, restaurantName: e.target.value }))}
              placeholder="e.g. Mike's Pizza Palace"
              className="pizza-input w-full text-sm" data-testid="contact-restaurant-input" />
          </div>

          {/* Double opt-in checkboxes */}
          <div className="bg-[#FFF8E7] rounded-lg p-3 border border-[#2A2A2A]/10 space-y-2">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.emailOptIn}
                onChange={e => setForm(f => ({ ...f, emailOptIn: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded border-2 border-[#2A2A2A] accent-[#E23D28]"
                data-testid="contact-email-optin" />
              <span className="text-xs text-[#2A2A2A]/70 leading-relaxed">
                Yes, I'd like to receive helpful tips on restaurant profitability, cost-saving strategies, and Pizza-lator updates via <strong>email</strong>. You can unsubscribe anytime.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.smsOptIn}
                onChange={e => setForm(f => ({ ...f, smsOptIn: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded border-2 border-[#2A2A2A] accent-[#E23D28]"
                data-testid="contact-sms-optin" />
              <span className="text-xs text-[#2A2A2A]/70 leading-relaxed">
                Yes, I consent to receive occasional <strong>SMS/text messages</strong> with tips and updates from Mikey Mozzarella's Pizza-lator. Message & data rates may apply. Reply STOP to opt out at any time. Max 4 msgs/month.
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-[#E23D28]">{error}</p>}

          <button type="submit" disabled={!isValid || loading}
            className="pizza-btn w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="contact-submit-btn"
          >
            {loading ? 'Saving...' : 'Download My Report'}
            {!loading && <Download className="w-4 h-4" />}
          </button>

          <div className="flex items-center justify-center gap-1.5 pt-1">
            <Shield className="w-3 h-3 text-[#2A2A2A]/30" />
            <p className="text-xs text-[#2A2A2A]/40 text-center">
              We respect your privacy. Your info is never sold or shared.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
